import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '@/app/routes';
import { ConfirmDialog, LoadingOverlay } from '@/components/feedback';
import { useTranslations } from '@/hooks';
import { ShipmentForm, EMPTY_SHIPMENT_FORM } from '../components/ShipmentForm';
import { useCreateIncomingShipmentMutation } from '../api/incomingShipmentsApi';
import { useShipmentFormOptions } from '../hooks';

// Inbounds ▸ Incoming shipments ▸ Add.
export const AddShipmentPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module } = useParams();

  const [createIncomingShipment, { isLoading: isSubmitting }] = useCreateIncomingShipmentMutation();

  const { options, isLoading } = useShipmentFormOptions();

  const [savedShipment, setSavedShipment] = useState(null);

  const goToShipments = useCallback(() => {
    navigate(ROUTES.incomingShipments(module));
  }, [navigate, module]);

  const goToShipmentsSaved = useCallback(() => {
    navigate(ROUTES.incomingShipments(module));
  }, [navigate, module]);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        setSavedShipment(await createIncomingShipment({ module, values }).unwrap());
      } catch {
        // notifyApiErrors already surfaced it.
      }
    },
    [createIncomingShipment, module],
  );

  const handleAddDeclarations = useCallback(() => {
    if (savedShipment?.id == null) {
      goToShipmentsSaved();
      return;
    }
    navigate(ROUTES.incomingShipmentAddDeclarations(module, savedShipment.id));
  }, [savedShipment, navigate, module, goToShipmentsSaved]);

  // Wait for the dropdowns: <Form> seeds initialValues once, on mount.
  if (isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  return (
    <>
      <ShipmentForm
        title={t('INCOMING_SHIPMENTS_ADD_TITLE')}
        submitLabel={t('COMMON_SAVE')}
        initialValues={EMPTY_SHIPMENT_FORM}
        onSubmit={handleSubmit}
        onClose={goToShipments}
        isSubmitting={isSubmitting}
        options={options}
      />

      <ConfirmDialog
        open={savedShipment != null}
        title={t('INCOMING_SHIPMENTS_ADDED_TITLE')}
        bodyTitle={t('ACTION_ADD_DECLARATIONS')}
        description={t('INCOMING_SHIPMENTS_ADDED_BODY', {
          shipmentId: savedShipment?.shipmentId ?? '',
        })}
        cancelLabel={t('ACTION_SKIP_FOR_NOW')}
        confirmLabel={t('ACTION_ADD_DECLARATIONS')}
        onCancel={goToShipmentsSaved}
        onConfirm={handleAddDeclarations}
      />
    </>
  );
};
